import { Component, OnInit, HostListener, ElementRef } from '@angular/core';

@Component({
    selector: 'app-calculator',
    templateUrl: './calculator.component.html',
    styleUrls: ['./calculator.component.scss'],
    standalone: false
})
export class CalculatorComponent implements OnInit {
  display: string = '0';
  expression: string = '';
  previousValue: string = '';
  operation: string = '';
  waitingForNewOperand: boolean = false;
  isVisible: boolean = false;
  showHistory: boolean = false;
  showResult: boolean = false;
  history: string[] = [];
  stayMode: boolean = false;

  // --- Context menu state ---
  contextMenuVisible = false;
  contextMenuPosition = { x: 0, y: 0 };

  // --- Drag state ---
  position = { top: 65, left: window.innerWidth - 330 }; // Default: top-right
  private isDragging = false;
  private dragStart = { x: 0, y: 0 };
  private dragOffset = { x: 0, y: 0 };

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void { }

  async copyResultToClipboard(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.display);
      // Optionally show feedback (toast, tooltip)
    } catch (err) {
      // Fallback for unsupported browsers
      this.fallbackCopyText(this.display);
    }
  }

  /** Fallback for clipboard.copy (execCommand) */
  fallbackCopyText(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // avoid scrolling
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      // Optionally show feedback
    } catch (err) {
      // Optionally show error
    }
    document.body.removeChild(textarea);
  }

  /** Paste from clipboard into calculator display */
  async pasteFromClipboard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      if (!isNaN(Number(text))) {
        this.display = text;
        this.expression = text;
      }
    } catch (err) {
      // Optionally show error
    }
  }

  // --- Context menu handlers ---
  onResultContextMenu(event: MouseEvent): void {
    event.preventDefault();
    this.contextMenuVisible = true;
    this.contextMenuPosition = { x: event.clientX, y: event.clientY };
  }

  closeContextMenu(): void {
    this.contextMenuVisible = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.contextMenuVisible) {
      this.closeContextMenu();
    }
    const calculatorContainer = this.elementRef.nativeElement.querySelector(
      '.calculator-container'
    );
    const calculatorIcon =
      this.elementRef.nativeElement.querySelector('.img_wrap');

    if (
      this.isVisible &&
      !this.stayMode &&
      calculatorContainer &&
      calculatorIcon
    ) {
      if (
        !calculatorContainer.contains(event.target as Node) &&
        !calculatorIcon.contains(event.target as Node)
      ) {
        this.removeCalculator();
      }
    }
  }

  // --- Drag handlers ---
  startDrag(event: MouseEvent): void {
    this.isDragging = true;
    this.dragStart = { x: event.clientX, y: event.clientY };
    this.dragOffset = { x: this.position.left, y: this.position.top };
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDrag(event: MouseEvent): void {
    if (this.isDragging) {
      const dx = event.clientX - this.dragStart.x;
      const dy = event.clientY - this.dragStart.y;
      this.position.left = this.dragOffset.x + dx;
      this.position.top = Math.max(0, this.dragOffset.y + dy);
    }
  }

  @HostListener('document:mouseup')
  stopDrag(): void {
    this.isDragging = false;
  }

  addCalculator(): void {
    this.isVisible = true;
  }

  removeCalculator(): void {
    this.isVisible = false;
    this.stayMode = false;
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory) {
      this.loadHistory();
    }
  }

  toggleStayMode(): void {
    this.stayMode = !this.stayMode;
  }

  inputNumber(num: string): void {
    this.showResult = false;

    if (this.waitingForNewOperand) {
      this.display = num;
      this.waitingForNewOperand = false;
      if (this.expression.endsWith('=')) {
        this.expression = num;
      } else {
        this.expression += num;
      }
    } else {
      this.display = this.display === '0' ? num : this.display + num;
      if (this.expression.endsWith('=')) {
        this.expression = num;
      } else {
        this.expression += num;
      }
    }
  }

  inputDecimal(): void {
    this.showResult = false;

    if (this.waitingForNewOperand) {
      this.display = '0.';
      this.waitingForNewOperand = false;
      if (this.expression.endsWith('=')) {
        this.expression = '0.';
      } else {
        this.expression += '0.';
      }
      return;
    }
    if (this.display.indexOf('.') === -1) {
      this.display += '.';
      this.expression += '.';
    }
  }

  clearAll(): void {
    this.display = '0';
    this.previousValue = '';
    this.operation = '';
    this.expression = '';
    this.waitingForNewOperand = false;
    this.showResult = false;
  }

  toggleSign(): void {
    if (this.display !== '0') {
      if (this.display.charAt(0) === '-') {
        this.display = this.display.slice(1);
        if (this.expression) {
          // Remove minus before last number
          const lastNumIndex = this.expression.lastIndexOf(
            '-',
            this.expression.length - this.display.length - 1
          );
          if (
            lastNumIndex !== -1 &&
            this.expression[lastNumIndex - 1] &&
            /[+\-×÷]/.test(this.expression[lastNumIndex - 1])
          ) {
            this.expression =
              this.expression.slice(0, lastNumIndex) +
              this.expression.slice(lastNumIndex + 1);
          }
        }
      } else {
        this.display = '-' + this.display;
        if (this.expression) {
          // Add minus before last number
          let idx = this.expression.length - this.display.length + 1;
          this.expression =
            this.expression.slice(0, idx) + '-' + this.expression.slice(idx);
        }
      }
    }
  }

  // Percentage
  percentage(): void {
    const value = parseFloat(this.display);
    this.display = (value / 100).toString();
    this.expression += '%';
  }

  performOperation(nextOperation: string): void {
    if (!this.expression.endsWith('=')) {
      this.showResult = false;
    }

    const inputValue = parseFloat(this.display);

    if (this.previousValue === '') {
      this.previousValue = this.display;
    } else if (this.operation) {
      const previousValue = parseFloat(this.previousValue);
      const result = this.calculate(previousValue, inputValue, this.operation);

      this.display = result.toString();
      this.previousValue = result.toString();
    }

    this.waitingForNewOperand = true;
    this.operation = nextOperation;
    if (this.expression.endsWith('=')) {
      this.expression = this.display + nextOperation;
    } else {
      if (/[+\-×÷]$/.test(this.expression)) {
        this.expression = this.expression.slice(0, -1) + nextOperation;
      } else {
        this.expression += nextOperation;
      }
    }
  }

  calculate(
    firstValue: number,
    secondValue: number,
    operation: string
  ): number {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return secondValue !== 0 ? firstValue / secondValue : 0;
      default:
        return secondValue;
    }
  }

  equals(): void {
    const inputValue = parseFloat(this.display);
    if (this.previousValue !== '' && this.operation) {
      const previousValue = parseFloat(this.previousValue);
      const result = this.calculate(previousValue, inputValue, this.operation);
      this.expression = `${this.previousValue}${this.operation}${this.display}=${result}`;
      this.display = result.toString();
      this.previousValue = '';
      this.operation = '';
      this.waitingForNewOperand = true;
      this.showResult = true;
      this.saveToHistory(this.expression);
    }
  }

  saveToHistory(expression: string): void {
    let history = JSON.parse(localStorage.getItem('calcHistory') || '[]');
    history = history.filter((item: string) => item !== expression);
    history.unshift(expression);
    localStorage.setItem('calcHistory', JSON.stringify(history));
  }

  loadHistory(): void {
    this.history = JSON.parse(localStorage.getItem('calcHistory') || '[]');
  }

  clearHistory(): void {
    localStorage.removeItem('calcHistory');
    this.history = [];
  }

  loadHistoryItem(item: string): void {
    const eqIndex = item.lastIndexOf('=');
    if (eqIndex !== -1) {
      this.expression = item.substring(0, eqIndex);
      this.display = item.substring(eqIndex + 1).trim();
      this.previousValue = '';
      this.operation = '';
      this.waitingForNewOperand = false;
      this.showResult = true;
    } else {

      this.expression = item;
      this.display = item;
      this.previousValue = '';
      this.operation = '';
      this.waitingForNewOperand = false;
      this.showResult = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isVisible) return;
    const key = event.key;
    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      this.inputNumber(key);
      return;
    }
    switch (key) {
      case '+':
        event.preventDefault();
        this.performOperation('+');
        break;
      case '-':
        event.preventDefault();
        this.performOperation('-');
        break;
      case '*':
        event.preventDefault();
        this.performOperation('×');
        break;
      case '/':
        event.preventDefault();
        this.performOperation('÷');
        break;
      case 'Enter':
      case '=':
        event.preventDefault();
        this.equals();
        break;
      case '.':
        event.preventDefault();
        this.inputDecimal();
        break;
      case 'Escape':
      case 'Delete':
        event.preventDefault();
        this.clearAll();
        break;
      case '%':
        event.preventDefault();
        this.percentage();
        break;
      case 'Backspace':
        event.preventDefault();
        this.backspace();
        break;
    }
  }

  backspace(): void {
    if (this.display.length > 1) {
      this.display = this.display.slice(0, -1);
      if (this.expression && !this.expression.endsWith('=')) {
        this.expression = this.expression.slice(0, -1);
      }
    } else {
      this.display = '0';
      if (this.expression && !this.expression.endsWith('=')) {
        this.expression = this.expression.slice(0, -1);
      }
    }
    this.showResult = false;
  }
}
